import Icon from '../../../../Icon/Icon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RenderPreview from './RenderPreview.js';
import { formatBytesAsMb } from '../creativeWeight.js';

function GroupContainer({
    groupHash,
    rotation,
    width,
    height,
    screen_count,
    slot_duration,
    motion_type,
    player,
    maxImageWeightBytes,
    maxVideoWeightBytes,
    screens,
    assigned_creatives,
    compatibleCreatives = [],
    onAssignCreative,
    canAssignCreatives = true,
    onUnlink,
    hasError,
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);

    // Informative: heavier creatives are rejected before upload.
    const maxImageWeight = formatBytesAsMb(maxImageWeightBytes);
    const maxVideoWeight =
        motion_type === 'STILL' ? null : formatBytesAsMb(maxVideoWeightBytes);
    const maxWeightParts = [
        maxImageWeight &&
            t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.max-weight-image',
                { weight: maxImageWeight }
            ),
        maxVideoWeight &&
            t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.max-weight-video',
                { weight: maxVideoWeight }
            ),
    ].filter(Boolean);
    const maxWeightLabel = maxWeightParts.length
        ? t(
              'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.max-weight',
              { limits: maxWeightParts.join(' · ') }
          )
        : null;
    const assignMenuRef = useRef(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [selectedView, setSelectedView] = useState(0);
    const [isAssignMenuOpen, setIsAssignMenuOpen] = useState(false);
    const [assigningCreativeHash, setAssigningCreativeHash] = useState(null);
    const totalSlides = assigned_creatives?.length ?? 0;

    // useEffect(() => {
    //     console.log('Should render GroupContainer', {
    //         rotation,
    //         width,
    //         height,
    //         screen_count,
    //         slot_duration,
    //         motion_type,
    //         screens,
    //         assigned_creatives,
    //     });
    // }, [assigned_creatives]);

    const updateCarouselPosition = () => {
        if (containerRef.current) {
            const slideWidth = containerRef.current.offsetWidth;
            const scrollAmount = currentSlideIndex * slideWidth;

            containerRef.current.scrollLeft = scrollAmount;
        }
    };

    const nextSlide = () => {
        const nextIndex = (currentSlideIndex + 1) % totalSlides;
        setCurrentSlideIndex(nextIndex);
    };

    const prevSlide = () => {
        const prevIndex = (currentSlideIndex - 1 + totalSlides) % totalSlides;
        setCurrentSlideIndex(prevIndex);
    };

    useEffect(() => {
        updateCarouselPosition();
    }, [currentSlideIndex]);

    useEffect(() => {
        window.addEventListener('resize', updateCarouselPosition);
        return () => {
            window.removeEventListener('resize', updateCarouselPosition);
        };
    }, []);

    useEffect(() => {
        if (!isAssignMenuOpen) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            if (
                assignMenuRef.current &&
                !assignMenuRef.current.contains(event.target)
            ) {
                setIsAssignMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isAssignMenuOpen]);

    function rotationString(rotation) {
        switch (rotation) {
            case 'plus90':
                return t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.rotation-right'
                );
            case 'minus90':
                return t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.rotation-left'
                );
            case 'upsideDown':
                return t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.rotation-upside-down'
                );
            default:
                return t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.rotation-normal'
                );
        }
    }

    function rotationDeg(rotation) {
        switch (rotation) {
            case 'plus90':
                return 90;
            case 'minus90':
                return -90;
            case 'upsideDown':
                return -180;
            default:
                return 0;
        }
    }

    function motionLabel(motion_type) {
        switch (motion_type) {
            case 'MOVING':
                return (
                    <>
                        <Icon name="film" size="1x" />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.motion-video'
                        )}
                        <Icon
                            name="image"
                            size="1x"
                            className="ml-1"
                        />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.motion-image'
                        )}
                    </>
                );
            case 'STILL':
                return (
                    <>
                        <Icon
                            name="image"
                            size="1x"
                            className="ml-1"
                        />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.motion-image-only'
                        )}
                    </>
                );
            default:
                return t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.motion-unknown'
                );
        }
    }

    function dynamicAdsLabel(player) {
        switch (player?.id) {
            // TODO: Add more player types that support dynamic ads, at the moment only "1" (LatinAd) is supported
            // This should be replaced with a proper check based on player
            // capabilities from an external source or configuration
            case 1:
                return (
                    <>
                        <Icon
                            name="code"
                            size="1x"
                            className="text-green-500"
                        />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.dynamic-ads-supported'
                        )}
                    </>
                );
            default:
                return (
                    <>
                        <Icon
                            name="times-square"
                            size="1x"
                            className="text-red-500"
                        />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.dynamic-ads-unsupported'
                        )}
                    </>
                );
        }
    }

    const renderAssignMenu = () => (
        <div
            ref={assignMenuRef}
            className="absolute top-2 left-2 z-20 flex justify-start">
            <button
                type="button"
                className="bg-blue-700 p-2 px-3 rounded-full text-white opacity-90 hover:opacity-100 transition duration-300 backdrop-blur-lg border shadow-md border-gray-50 border-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!canAssignCreatives || Boolean(assigningCreativeHash)}
                onClick={() => {
                    if (!canAssignCreatives) {
                        return;
                    }

                    setIsAssignMenuOpen((currentValue) => !currentValue);
                }}
                dataTestid="GroupContainer-open-assign-dropdown">
                <Icon name="link" size="sm" />
            </button>

            {isAssignMenuOpen && (
                <div
                    className="absolute top-12 left-0 w-64 max-w-xs rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden"
                    dataTestid="GroupContainer-assign-dropdown">
                    <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.compatible-creatives'
                        )}
                    </div>

                    {compatibleCreatives.length > 0 ? (
                        <ul className="max-h-64 overflow-y-auto">
                            {compatibleCreatives.map((creative) => (
                                <li key={creative.properties?.hash}>
                                    <button
                                        type="button"
                                        className="w-full px-3 py-3 text-left hover:bg-blue-50 transition border-b border-gray-100 last:border-b-0 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={
                                            !canAssignCreatives ||
                                            Boolean(assigningCreativeHash)
                                        }
                                        onClick={async () => {
                                            const nextCreativeHash =
                                                creative.properties?.hash;

                                            if (!nextCreativeHash) {
                                                return;
                                            }

                                            setAssigningCreativeHash(
                                                nextCreativeHash
                                            );

                                            try {
                                                const linked =
                                                    await onAssignCreative?.(
                                                        groupHash,
                                                        nextCreativeHash
                                                    );

                                                if (linked) {
                                                    setIsAssignMenuOpen(false);
                                                }
                                            } finally {
                                                setAssigningCreativeHash(null);
                                            }
                                        }}>
                                        <div className="text-sm font-medium text-gray-800 truncate">
                                            {creative.displayName}
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500 uppercase">
                                            <span>
                                                {creative.properties?.type}
                                            </span>
                                            <span>
                                                {creative.properties?.width} x{' '}
                                                {creative.properties?.height}
                                            </span>
                                            {creative.properties?.type ===
                                                'video' && (
                                                <span>
                                                    {Math.round(
                                                        creative.properties
                                                            ?.duration || 0
                                                    )}
                                                    s
                                                </span>
                                            )}
                                            {assigningCreativeHash ===
                                                creative.properties?.hash && (
                                                <span className="text-blue-600">
                                                    {t(
                                                        'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.saving'
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="px-3 py-4 text-sm text-gray-500">
                            {t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.no-compatible-creatives'
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div
            className={`relative flex flex-col border ${
                hasError ? 'border-red-500' : ' border-gray-300'
            } rounded-lg p-4 w-full hover:shadow-xl hover:border-blue-500 transition-shadow transition-all duration-200 `}
            dataTestid="GroupContainer">
            <div className="font-light text-sm mb-3 overflow-hidden truncate whitespace-nowrap flex flex-row justify-between items-center">
                <div
                    className="text-normal font-semibold"
                    dataTestid="GroupContainer-size">
                    {width}x{height} - {rotationString(rotation)}
                </div>
                <div className="flex items-center gap-2">
                    <div
                        className="inline-block bg-blue-400 text-gray-50 text-xs px-2 pt-0.5 rounded-full font-bold"
                        dataTestid="GroupContainer-screens-count">
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.screens-count',
                            { count: screen_count }
                        )}
                    </div>
                    {(assigned_creatives?.length ?? 0) > 0 && (
                        <div
                            className="inline-block bg-green-500 text-white text-xs px-2 pt-0.5 rounded-full font-bold"
                            dataTestid="GroupContainer-assigned-count">
                            <Icon
                                name="file"
                                size="sm"
                                className="mr-1"
                            />
                            {t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.assigned-count',
                                { count: assigned_creatives?.length ?? 0 }
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/** Properties header */}
            <div className="flex justify-between items-center mb-1 text-xs">
                <div
                    className="flex items-center gap-1"
                    dataTestid="GroupContainer-slot-duration">
                    <Icon name="clock" size="1x" />
                    {slot_duration}s
                </div>
                <div
                    className="flex items-center gap-1"
                    dataTestid="GroupContainer-player-support">
                    {dynamicAdsLabel(player)}
                </div>
            </div>
            <div
                className="flex justify-end items-center mb-3 text-xs"
                dataTestid="GroupContainer-motion-type">
                <div className="flex items-center gap-1">
                    {motionLabel(motion_type)}
                </div>
            </div>
            {maxWeightLabel && (
                <div
                    className="flex items-center gap-1 -mt-2 mb-3 text-xs text-gray-600"
                    dataTestid="GroupContainer-max-weight">
                    <Icon name="file" size="1x" />
                    {maxWeightLabel}
                </div>
            )}

            <div className="top-4 right-4 flex space-x-2 z-10">
                <ul
                    className="flex space-x-2 mb-2"
                    dataTestid="GroupContainer-view-selector">
                    <li
                        className={`bg-white p-2 rounded-lg shadow-md cursor-pointer hover:bg-gray-300 transition ${
                            selectedView == 0 ? 'bg-gray-300' : ''
                        }`}
                        onClick={() => setSelectedView(0)}>
                        <Icon
                            name="photo-video"
                            size="sm"
                            className="ml-1"
                        />
                    </li>
                    <li
                        className={`bg-white p-2 rounded-lg shadow-md cursor-pointer hover:bg-gray-300 transition ${
                            selectedView == 1 ? 'bg-gray-300' : ''
                        }`}
                        onClick={() => setSelectedView(1)}>
                        <Icon name="list" size="lg" />
                    </li>
                </ul>
            </div>

            {selectedView == 0 && (
                <div
                className="relative w-full max-w-4xl mx-auto shadow-xl rounded-lg overflow-visible border border-gray-300"
                style={{ aspectRatio: `${width} / ${height}` }}>
                    {compatibleCreatives.length > 0 &&
                        (assigned_creatives?.length ?? 0) === 0 &&
                        renderAssignMenu()}
                    <div
                        ref={containerRef}
                        className="flex overflow-x-hidden scroll-smooth h-full"
                        style={{ scrollSnapType: 'x mandatory' }}
                        dataTestid="GroupContainer-creative-carousel">
                        {assigned_creatives?.length == 0 && (
                            <div
                                className={`relative flex-shrink-0 w-full h-full flex items-center justify-center overflow-visible align-middle`}
                                style={{
                                    scrollSnapAlign: 'start',
                                }}>
                                <div
                                    className={`flex items-center justify-center gap-2 text-center text-gray-400 ${
                                        hasError
                                            ? 'text-red-500'
                                            : ' text-gray-400'
                                    }`}>
                                    <Icon
                                        name="exclamation-triangle"
                                        size={16}
                                    />
                                    <span>
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-creatives.creatives.group-card.no-assigned-creatives'
                                        )}
                                    </span>
                                </div>
                            </div>
                        )}

                        {assigned_creatives?.map((slide) => {
                            return (
                                <div
                                    key={slide.properties.hash}
                                    className={`relative flex-shrink-0 w-full h-full flex items-center justify-center overflow-hidden align-middle`}
                                    style={{
                                        scrollSnapAlign: 'start',
                                    }}>
                                    <RenderPreview
                                        previewType={slide.properties.type}
                                        previewProperties={{
                                            rotation: rotation,
                                            width: width,
                                            height: height,
                                        }}
                                        previewSrc={
                                            slide.localPreviewUrl ||
                                            slide.remoteUrl
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="absolute top-2 right-2 bg-red-700 p-2 px-3 rounded-full text-white opacity-80 hover:opacity-100 transition duration-300 z-10 backdrop-blur-lg border shadow-md border-gray-50 border-opacity-50"
                                        onClick={() =>
                                            onUnlink?.(
                                                groupHash,
                                                slide.properties.hash
                                            )
                                        }>
                                        <Icon
                                            name="unlink"
                                            size="sm"
                                        />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {assigned_creatives?.length > 1 && (
                        <>
                            <button
                                onClick={prevSlide}
                                className="absolute top-1/2 left-1 transform -translate-y-2/3 bg-gray-900 p-3 rounded-full text-white opacity-70 hover:opacity-90 transition duration-300 z-10 backdrop-blur-lg border shadow-md border-gray-50 border-opacity-50"
                                dataTestid="GroupContainer-creative-carousel-prev">
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="3"
                                        d="M15 19l-7-7 7-7"></path>
                                </svg>
                            </button>

                            <button
                                onClick={nextSlide}
                                className="absolute top-1/2 right-1 transform -translate-y-2/3 bg-gray-900 p-3 rounded-full text-white opacity-70 hover:opacity-90 transition duration-300 z-10 backdrop-blur-lg border shadow-md border-gray-50 border-opacity-50"
                                dataTestid="GroupContainer-creative-carousel-next">
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="3"
                                        d="M9 5l7 7-7 7"></path>
                                </svg>
                            </button>
                        </>
                    )}
                </div>
            )}
            {selectedView == 1 && (
                <div className="p-2 relative w-full max-w-4xl mx-auto shadow-xl rounded-lg h-48 border border-gray-300 overflow-y-auto">
                    <ul
                        className="flex flex-col scroll-smooth h-48"
                        dataTestid="GroupContainer-creative-carousel-screen-list">
                        {screens?.map((screen) => (
                            <li
                                key={screen.id}
                                className="flex items-center justify-start p-1 gap-2 border-b border-gray-200 max-w-full text-clip text-clip">
                                <div>
                                    <span className="font-bold bg-gray-700 rounded-full mr-2 text-white text-xs px-2 py-1">
                                        {screen.external_id}
                                    </span>
                                    <span className="text-clip">
                                        {screen.alias}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default GroupContainer;
